'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type OrbitingCirclesProps = {
  children: React.ReactNode[] | React.ReactNode;
  className?: string;
  radius?: number; // px distance from center
  speed?: number; // seconds per full rotation
  reverse?: boolean;
  iconSize?: number; // px square for each child wrapper
};

export function OrbitingCircles({
  children,
  className,
  radius = 140,
  speed = 18,
  reverse = false,
  iconSize = 40,
}: OrbitingCirclesProps) {
  const items = React.Children.toArray(children);
  const count = Math.max(1, items.length);

  const diameter = radius * 2 + iconSize;

  return (
    <div className={cn('relative isolate', className)} style={{ width: diameter, height: diameter }}>
      <motion.div
        aria-hidden
        className="absolute inset-0"
        animate={{ rotate: reverse ? -360 : 360 }}
        transition={{ repeat: Infinity, ease: 'linear', duration: Math.max(2, speed) }}
        style={{ transformOrigin: '50% 50%', width: diameter, height: diameter }}
      >
        {items.map((child, i) => {
          const angle = (i / count) * 360;
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{
                width: iconSize,
                height: iconSize,
                transform: `translate(-50%, -50%) rotate(${angle}deg) translate(${radius}px) rotate(${-angle}deg)`,
              }}
            >
              <div className="flex h-full w-full items-center justify-center rounded-full bg-white text-black shadow-sm">
                {child}
              </div>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}
